<template>
  <div class="flex flex-col gap-3">
    <!-- Feature Items -->
    <div
      v-for="(feature, index) in includedFeatures"
      :key="feature.id"
      class="flex items-center gap-3 bg-neutral-white rounded-lg"
      draggable="true"
      @dragstart="handleDragStart(index, $event)"
      @dragover.prevent
      @drop="handleDrop(index)"
      @dragenter.prevent
    >
      <PulseButton variant="secondary" size="small" class="cursor-move">
        <IconDrag class="w-4 h-4 fill-current" />
      </PulseButton>

      <PulseToggle v-model="feature.enabled" />

      <PulseInput v-model="feature.text" placeholder="Feature text" class="flex-1" />

      <PulseButton variant="secondary" size="small" @click="removeFeature(index)">
        <LineTrashAlt class="w-4 h-4 fill-current" />
      </PulseButton>
    </div>

    <!-- Add new item button -->
    <PulseButton variant="secondary" class="mt-2" @click="addNewFeature"> Add new item </PulseButton>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { IconDrag, LineTrashAlt } from '@pulse/components/icons';
import cloneDeep from 'lodash/cloneDeep';

interface IncludedFeature {
  id: string;
  text: string;
  enabled: boolean;
}

// Define props and emits for v-model
const props = defineProps<{
  modelValue: IncludedFeature[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: IncludedFeature[]): void;
}>();

// Use computed for two-way binding
const includedFeatures = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

// Drag and drop state
const draggingIndex = ref<number | null>(null);

// Add new feature
function addNewFeature() {
  const newId = (includedFeatures.value.length + 1).toString();
  const newFeatures = [
    ...includedFeatures.value,
    {
      enabled: true,
      id: newId,
      text: '',
    },
  ];
  includedFeatures.value = newFeatures;
}

// Remove feature
function removeFeature(index: number) {
  const newFeatures = [...includedFeatures.value];
  newFeatures.splice(index, 1);
  includedFeatures.value = newFeatures;
}

// Start drag
function handleDragStart(index: number, event: DragEvent) {
  draggingIndex.value = index;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
  }
}

// Handle drop
function handleDrop(dropIndex: number) {
  if (draggingIndex.value === null || draggingIndex.value === dropIndex) return;

  const features = cloneDeep(includedFeatures.value);
  const fromIndex = draggingIndex.value;

  const [draggedFeature] = features.splice(fromIndex, 1);
  features.splice(dropIndex, 0, draggedFeature);

  includedFeatures.value = features;
  draggingIndex.value = null;
}
</script>

<style scoped>
/* Override hardcoded fill="#1D2433" on IconDrag path for dark mode */
:deep(.cursor-move svg path) {
  fill: currentColor !important;
}
</style>
